import configPreferences from "csi/config/preferences";
import configuration from "csi/configuration";
import html from "csi/html";
import csi from "csi/main";
import message from "csi/message";
import ComparePageStore from "csi/page/ComparePageStore";
import { getNodeClass } from "csi/reflection/reflection";
import { setLocationTab, setSessionRollupVariables } from "csi/router";
import { onNavigateToUrl } from "csi/service/iframeService";
import { enableDebugMode } from "csi/service/layoutService";
import { CONVERSATIONS, close3dViewer, resetCamundaAllowedTypes } from "csi/service/rightPaneService";
import session from "csi/session";
import { dataSetupStore } from "csi/state/datasetup";
import rootStore, { browserStore, localStore, rightPaneStore, routerStore, sessionStore } from "csi/state/rootStore";
import { DASH_SEPARATOR } from "csi/state/viewUtils";
import { C8Node, cnl } from "csi/typings";
import { LOCATION_TAB } from "csi/utility/routerUtility";
import { stringCombine } from "csi/utility/stringUtility";
import { disableDojoDialogFocus } from "csi/widget/Dialog";
import topic from "dojo/topic";
import { action, autorun, reaction } from "mobx";
import { clearAggregateStoreResponsesBy, setQueryAggregateStoreGlobalParameters } from "src/c8-platform/service/aggregateStoreService";

/**
 * Update browser title when it changes
 */
autorun(() => {
	document.title = stringCombine(routerStore.locationTitle, csi.productName(), " - ");
});

/**
 * Update rollup scope as session changes
 */
autorun(setSessionRollupVariables);

/**
 * Watch for changes to see if comment and threeDStore stores exist if so pins are only allowed in the Style viewer when the sessionStore, allow 3D annotations is true
 */
autorun(() => {
	const { commentsStore, threeDStore } = rootStore;
	commentsStore?.setAllowPins(!!threeDStore?.allowPins);
});

/**
 * When editing inline, we need to disable focus on Dojo dialogs
 */
reaction(
	() => browserStore.editingInline,
	(value) => {
		disableDojoDialogFocus(value);
	},
);


/**
 * Set visibility of 3d viewer based on state change
 * Set 3D Pin values on comments store
 * on click pin select the corresponding comments
 * on hover pin highlight the corresponding comment
 */
reaction(
	() => rootStore.threeDStore,
	(threeDStore) => {
		const { commentsStore } = rootStore;
		html.setVisible(html.pageContent(), !threeDStore);
		routerStore.setCenterFloatComponent(threeDStore ? "csi/threeD/threeDViewerDialog" : null);
		if (threeDStore && commentsStore) {
			commentsStore.onAddPin = threeDStore.annotationsStore.addPin;
			commentsStore.onRemovePin = threeDStore.annotationsStore.removePin;
			commentsStore.onCancelNewPin = threeDStore.annotationsStore.setAnnotationPins;

			threeDStore.annotationsStore.onPinClick = action((commentUrl: cnl) => {
				if (commentsStore.selectedCommentUrl === commentUrl) {
					commentsStore.backToComments();
					return;
				}
				commentsStore.selectCommentByUrl(commentUrl);
				rightPaneStore.setSelected(CONVERSATIONS);
			});

			threeDStore.annotationsStore.onPinHover = action((commentUrl: cnl) => {
				commentsStore.setHoveredCommentByUrl(commentUrl);
			});
		}
	},
);

/**
 * Sets pin position on 3d view
 */
reaction(
	() => rootStore.threeDStore?.annotationsStore.pinPosition,
	() => {
		const { commentsStore, threeDStore } = rootStore;
		if (threeDStore) {
			if (threeDStore.annotationsStore.resetPin) {
				commentsStore.cancelUpdatePin();
			} else {
				commentsStore.setPinPosition(threeDStore.annotationsStore.pinPosition);
			}
			threeDStore.annotationsStore.setResetPin(false);
		}
	},
);

/**
 * Sets pin to movable mode while adding new pin or updating pin position
 * prevent the pin from getting saved in center of model
 */
reaction(
	() => rootStore.threeDStore?.annotationsStore.pinIsMoving,
	() => {
		const { commentsStore, threeDStore } = rootStore;
		if (commentsStore && threeDStore) {
			const { pinIsMoving } = threeDStore.annotationsStore;
			const { cancelNewPin, isPinOnModel, newPin, setPinIsMoving } = commentsStore;

			if (newPin && !pinIsMoving && !isPinOnModel) {
				cancelNewPin();
			}

			setPinIsMoving(pinIsMoving);
		}
	},
);

/**
 * Load model with available pins
 * Sets the pin clickabilty on 3D Viewer based on detailed view or summary view
 */
autorun(() => {
	const { commentsStore, threeDStore } = rootStore;
	if (threeDStore && commentsStore) {
		const { setAnnotationPins, setPinClickable } = threeDStore.annotationsStore;
		const { availablePinData, selectedCommentUrl } = commentsStore;

		setPinClickable(!selectedCommentUrl);
		setAnnotationPins(availablePinData, selectedCommentUrl);
	}
});

/**
 * If the 3D viewer is open, and there has been a change in location, close the 3D viewer
 */
reaction(
	() => routerStore.targetRoute,
	() => close3dViewer(false),
);

/***
 * When a comment is hovered in the comments list, highlight the corresponding pin in the 3D viewer
 */
reaction(
	() => rootStore.commentsStore?.hoveredCommentUrl,
	(value) => {
		const { threeDStore, commentsStore } = rootStore;
		if (threeDStore && !commentsStore?.selectedCommentUrl && !commentsStore?.isEditing) {
			threeDStore.annotationsStore.setAnnotationActive(value);
		}
	},
);

/**
 * When a pin color is changed, update the pin color in the 3D viewer
 */
reaction(
	() => rootStore.commentsStore?.forcePinColorUpdate,
	(value) => {
		if (!value) {
			return;
		}
		const { threeDStore, commentsStore } = rootStore;
		if (threeDStore && commentsStore) {
			const { newPin, pinColor } = commentsStore;
			threeDStore.annotationsStore.updateEditingPinColor(newPin, pinColor);
			commentsStore.setForcePinColorUpdate(false);
		}
	},
);

/**
 * Exit from pin edit mode
 */
reaction(
	() => rootStore.commentsStore?.pinEditMode,
	() => {
		const { commentsStore, threeDStore } = rootStore;
		if (threeDStore) {
			if (!commentsStore.pinEditMode) {
				threeDStore.annotationsStore.exitPinEditMode();
			}
			else {
				threeDStore.annotationsStore.setPinIsMoving(true);
			}
		}
	},
);

/**
 * Track selected tab and its name for current location and title
 */
reaction(
	() => rootStore.layoutStore?.selectedItem,
	(value) => {
		if (value) {
			localStore.viewHistory[rootStore.layoutStore.historyKey] = value.id;
			routerStore.tabName = value.name;
			session.setLocationActual(LOCATION_TAB, value.id, null, setLocationTab, true);
			message.hide();
		}
	},
);

/**
 * Add debug tab to layout if currently on site page when debug mode is enabled
 */
reaction(
	() => sessionStore.debugMode,
	(value) => {
		const { layoutStore } = rootStore;

		if (layoutStore && value) {
			enableDebugMode(layoutStore);
		}

		setQueryAggregateStoreGlobalParameters(value);
	},
);

/**
 * Set editLocale to locale if not already initialized
 */
reaction(
	() => sessionStore.locale,
	(value) => {
		localStore.editLocale = localStore.editLocale || value;
	},
);

/**
 * Notify parent application when navigation occurs if embedded in iframe
 */
reaction(
	() => routerStore.locationUrl,
	(value) => {
		onNavigateToUrl(value);
	},
);

/**
 * Update revision url used for comments when navigating to a new revision
 */
reaction(
	() => routerStore.locationRevisionUrl,
	() => {
		const { commentsStore } = rootStore;
		const { locationRevisionNode, locationUrl } = routerStore;

		if (commentsStore?.primaryUrl === locationUrl) {
			commentsStore.setRevisionNodeInView(locationRevisionNode);
		}
	},
);

/**
 * Reset ad-hoc print whenever print is reset
 */
reaction(
	() => sessionStore.print,
	(value) => {
		if (!value) {
			sessionStore.printAdHoc = false;
		}
	},
);

/**
 * Update user default values when user defaults node is deleted
 */
reaction(
	() => sessionStore.userDefaults,
	(value) => {
		configPreferences.setUserDefaultValues(value);
	},
);

/**
 * If adminOnly switches, call the configuration refresh
 */
reaction(
	() => sessionStore.adminOnly,
	() => {
		configuration.refresh();
	},
);

/**
 * update title for data setup instance
 */
reaction(
	() => dataSetupStore.activeItem?.name + dataSetupStore.activeSection?.name,
	(value) => {
		if (!!value) {
			routerStore.locationTitleForDataSetup = stringCombine(dataSetupStore.activeItem.name, dataSetupStore.activeSection.name, DASH_SEPARATOR);
		}
	},
);

/** React to the compare mode changing for ComparePageStore by hiding / un-hiding the breadcrumb */
reaction(
	() => {
		const { pageStore } = rootStore;
		if (pageStore) {
			const comparePageStore = pageStore as ComparePageStore;
			return !!comparePageStore.isCompareMode;
		}
		return false;
	},
	(value) => {
		routerStore.hideBreadcrumb = routerStore.hideTabs = value;
	},
);

/**
 * Clear aggregate store cache of responses that includes nodes of the given type to
 * ensure that data does not become stale (too often)
 */
export function clearAggregateStoreResponsesForNode(node: C8Node): void {
	const { $PT } = node;
	const rc = getNodeClass(node);
	const rcMaster = rc?.getMasterClass();

	clearAggregateStoreResponsesBy(({ groups, time }) => {
		if ($PT < (time || 0)) {
			return false;
		}

		return groups.some(({ nodeType }) => {
			// it is possible that a group's node type is abstract (i.e. ProductColorSpec), so make sure to check any base classes too with isDerivedFrom;
			// in addition, it is possible the group contains master nodes, but a change was made to the revision. In most cases, selecting a master node
			// will include a filter testing a condition on the revision node.
			return rc.isDerivedFrom(nodeType) || rcMaster?.isDerivedFrom(nodeType);
		});
	});
}


/**
 * Track whether mouse is up or down
 */
export function listenForMouseUpDown(): void {
	window.addEventListener("mousedown", action(() => browserStore.isMouseDown = true));
	window.addEventListener("mouseup", action(() => browserStore.isMouseDown = false));
}

/**
 * When new process template is published, make sure to update allowed types
 */
topic.subscribe("/csi/breadcrumb/simple/approve", async (node: C8Node) => {
	if (node.$Type === "CamundaProcessDefinition") {
		resetCamundaAllowedTypes();
	}
});
